<template>
    <v-list-item
        component="CharacterListItem"
        :class="[
            { selected: isCharacterSelected(character) },
            { confirm: confirmCharacterDelete },
            { isSmallScreen: getIsSmallScreen },
            { isSmallerScreen: getIsSmallerScreen },
            { isInOrder: !canEdit },
        ]"
        :style="{ background: `${getColorById(character.farba)}!important` }"
    >
        <div class="name" @click="selectCharacter(character)" @dblclick="showInput">
            <div class="text" v-if="!isEditable">{{ character.meno }}</div>
            <v-text-field
                v-else
                v-model="character_meno"
                class="input"
                autofocus
                hide-details
                @keyup.esc="hideInput"
                @keyup.enter="hideInput"
                @focusout="hideInput"
                @change="finishEditing"
            ></v-text-field>
        </div>

        <div class="action">
            <mp3-player
                v-if="projectCharacterHasCandidates(character)"
                class="sample ml-auto"
                :file="getMp3(character)"
                :size="getIsSmallerScreen ? 'smaller' : 'small'"
                :visualizer="'project'"
            ></mp3-player>
            <btn
                :size="getIsSmallerScreen ? 'iconSmall' : 'icon'"
                color="transparent"
                :icon="'close'"
                v-if="canEditProject && canEdit"
                :class="{ 'ml-auto': !projectCharacterHasCandidates(character) }"
                @click.stop="confirmCharacterDelete = true"
            />
        </div>

        <div
            class="warning"
            @click.stop=""
            :class="{ show: confirmCharacterDelete }"
            :style="{ background: `linear-gradient(90deg, transparent, ${charColor} 40%)!important` }"
        >
            <div class="label">Remove character?</div>
            <btn
                :icon="'close'"
                color="ghost"
                for-background="dark"
                :size="getIsSmallerScreen ? 'iconSmall' : 'icon'"
                @click.stop="confirmCharacterDelete = false"
            />
            <btn
                :icon="'check'"
                color="ghost"
                for-background="dark"
                :size="getIsSmallerScreen ? 'iconSmall' : 'icon'"
                @click.stop="removeFromList(character)"
            />
        </div>
    </v-list-item>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex';
import draggable from 'vuedraggable';
import Mp3Player from '@/components/.global/Mp3Player.vue';

export default {
    name: 'CharacterListItem',

    props: {
        character: {},
        canEdit: {
            type: Boolean,
            default: true,
        },
    },

    data() {
        return {
            confirmCharacterDelete: false,
            fatalError: false,
            loading: true,
            creating_new_character: false,
            isEditable: false,
            character_meno: null,
            dragOptions: {
                ghostClass: 'ghost',
            },
        };
    },

    async mounted() {
        this.character_meno = this.character.meno;
        this.loading = false;
    },

    methods: {
        ...mapActions(['removeCharacter', 'setCharacter', 'setCharacterToActors']),

        showInput() {
            if (!this.canEdit) return;
            this.isEditable = true;
        },

        hideInput() {
            this.isEditable = false;
        },

        finishEditing() {
            // eslint-disable-next-line vue/no-mutating-props
            this.character.meno = this.character_meno;
            this.hideInput();
        },

        async removeFromList(character) {
            this.setCharacterToActors({
                voices: character.candidates,
                characterId: null,
            });

            await this.removeCharacter(character);

            if (this.project.characters.length > 0) {
                this.setCharacter(this.project.characters[0]);
            } else {
                this.setCharacter({});
            }
        },

        getMp3(character) {
            if (character.candidates.length > 0) {
                return this.getSampleMp3Url(this.getProjectCharacterMp3(character));
            }
        },

        isCharacterSelected(character) {
            return this.getSelectedCharacter.id === character.id;
        },

        selectCharacter(character) {
            if (character.id === this.getSelectedCharacter.id) {
                return;
            }
            this.setCharacter(character);
        },
    },

    computed: {
        ...mapGetters({
            project: 'getProject',
            projectCharacterHasCandidates: 'projectCharacterHasCandidates',
            getProjectCharacterMp3: 'getProjectCharacterMp3',
            getSampleMp3Url: 'getSampleMp3Url',
            getSelectedCharacter: 'getSelectedCharacter',
            canEditProject: 'canEditProject',
            getColorById: 'getColorById',
            getIsSmallScreen: 'getIsSmallScreen',
            getIsSmallerScreen: 'getIsSmallerScreen',
        }),

        charColor() {
            return this.getColorById(this.character.farba);
        },
    },

    watch: {},

    components: {
        draggable,
        Mp3Player,
    },
};
</script>

<style lang="less">
*::-webkit-scrollbar-track {
    -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
}

*::-webkit-scrollbar {
    width: 10px;
    background-color: transparent;
}

*::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.2);
    border: none;
}
</style>

<style lang="less" scoped>
@import '~@variables';

[component='CharacterListItem'] {
    position: relative;
    align-items: initial;
    overflow: hidden;
    min-height: 40px;
    padding: 0 8px 0 0;

    &.isInOrder {
        .name {
            cursor: move;
        }

        .action {
            cursor: move;
        }
    }

    &.isSmallerScreen {
        min-height: 33px;
        height: 33px;
        padding: 0 5px 0 0;

        .name {
            font-size: 13px;
        }

        .warning {
            padding-right: 5px;
        }
    }

    &:before {
        .fullFillBground;
        background-color: white;
        opacity: 0;
        pointer-events: none;
        transition: 0.3s;
    }

    &:hover {
        &:before {
            opacity: 0.15;
        }
    }

    &.confirm {
        .name {
            opacity: 0.3;
        }
    }

    &.selected {
        .name .text {
            background: fade(white, 30%);
        }
    }

    .name {
        .flex-row;
        cursor: pointer;
        width: 70%;
        color: black;
        padding-left: 10px;

        .text {
            font-size: 15px;
            font-weight: 700;
            overflow: hidden;
            margin: 0;
            white-space: nowrap;
            text-overflow: ellipsis;
            background: fade(white, 0%);
            padding: 0 7px;
            border-radius: 10px;
        }

        .v-input {
            padding: 0;
            margin: 5px 0 0;
        }
    }

    .action {
        position: relative;
        .flex-row;
        width: 30%;
    }

    .warning {
        .flex-row;
        justify-content: flex-end;
        position: absolute;
        right: 0;
        height: 100%;
        width: 100%;
        z-index: 6;
        transition: 0.3s;
        transform: translateX(110%);
        padding: 10px 10px 10px 30px;
        pointer-events: all;

        &.show {
            transform: translateX(0);
        }

        .label {
            font-weight: 700;
            color: white;
            margin-right: 10px;
        }

        button {
            margin-left: 3px;
        }
    }
}
</style>
